<template>
  <div class="tw:flex tw:flex-col tw:w-full">
    <!-- Main body: two columns -->
    <div class="tw:flex tw:flex-col tw:md:flex-row">
      <!-- Left column: content -->
      <div class="tw:flex tw:flex-col tw:gap-y-5 tw:flex-1 tw:overflow-y-auto tw:md:overflow-visible">
        <p class="tw:text-sm tw:text-gray-600">
          {{ $t('subscription_reactivation_subtext') }}
        </p>

        <!-- New features with Plus -->
        <div>
          <div class="tw:flex tw:items-center tw:gap-2 tw:mb-3">
            <span
              class="tw:bg-decorative-red tw:text-white tw:text-xs tw:font-bold tw:px-2 tw:py-0.5 tw:rounded-full tw:leading-tight"
              >{{ $t('business_studio_plus_new_label') }}</span
            >
            <p class="tw:font-bold tw:text-sm tw:text-gray-900">
              {{ $t('business_studio_plus_features_header') }}
            </p>
          </div>
          <ul class="tw:flex tw:flex-col tw:gap-y-2.5">
            <li
              v-for="feature in newFeatures"
              :key="feature.labelKey"
              class="tw:flex tw:items-center tw:gap-2.5 tw:text-sm tw:text-gray-700"
            >
              <img :src="feature.icon" class="tw:w-5 tw:h-5 tw:shrink-0" alt="" aria-hidden="true" />
              {{ $t(feature.labelKey) }}
            </li>
          </ul>
        </div>

        <!-- Included from Pro Package -->
        <div>
          <p class="tw:font-bold tw:text-sm tw:text-gray-900 tw:mb-3">
            {{ $t('subscription_features_header') }}
          </p>
          <div class="tw:grid tw:grid-cols-2 tw:gap-3">
            <div
              v-for="feature in proFeatures"
              :key="feature.labelKey"
              class="tw:flex tw:items-center tw:gap-2.5 tw:text-sm tw:font-bold tw:text-gray-700 tw:border tw:border-gray-200 tw:rounded-lg tw:px-3 tw:py-2.5 tw:bg-gray-50"
            >
              <img :src="feature.icon" class="tw:w-5 tw:h-5 tw:shrink-0" alt="" aria-hidden="true" />
              {{ $t(feature.labelKey) }}
            </div>
          </div>
        </div>
      </div>

      <!-- Right column: video (desktop only) -->
      <div
        class="tw:hidden tw:md:flex tw:items-end tw:justify-center tw:w-[260px] tw:shrink-0 tw:overflow-hidden tw:pl-6"
      >
        <StudioPlusHeroVideo
          class="tw:w-full tw:h-auto tw:max-w-[320px] tw:md:max-w-none tw:md:h-[250px] tw:block"
        />
      </div>
    </div>

    <!-- Pricing band -->
    <div class="tw:py-4">
      <div class="tw:bg-green-100 tw:p-3 tw:rounded-lg">
        <p class="tw:text-xs tw:font-bold tw:text-gray-700 tw:tracking-wide tw:mb-1">
          {{ $t('subscription_price_new') }}
        </p>
        <div v-if="isPriceLoading" class="tw:h-8 tw:flex tw:items-center">
          <SmallDotLoader :color="hasSavings ? '#15803d' : '#374151'" />
        </div>
        <div v-else class="tw:flex tw:flex-wrap tw:items-baseline tw:gap-x-2 tw:gap-y-1">
          <span :class="hasSavings ? 'tw:text-green-700' : 'tw:text-gray-900'" class="tw:text-3xl tw:font-bold">
            {{ formatCurrency(plusMonthlyPrice) }}
          </span>
          <span v-if="isPricingComparison" class="tw:text-base tw:text-gray-400 tw:line-through">{{
            formatCurrency(proPrice, { divideBy100: false })
          }}</span>
        </div>
        <p v-if="isPricingComparison && monthlySavings > 0" class="tw:text-sm tw:font-bold tw:text-green-700 tw:mt-1">
          {{
            $t('subscription_monthly_savings', {
              SavingsAmount: formatCurrency(monthlySavings, { divideBy100: false }),
            })
          }}
        </p>
        <i18n-t keypath="subscription_price" tag="p" class="tw:text-xs tw:text-gray-500 tw:mt-1">
          <template #SubscriptionPrice>{{ formatCurrency(annualPrice) }}</template>
        </i18n-t>
        <p class="tw:text-xs tw:text-gray-500">{{ $t('subscription_voucher_info') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

import SmallDotLoader from '@/components/base/loaders/SmallDotLoader.vue'
import StudioPlusHeroVideo from '@/components/studioPlus/StudioPlusHeroVideo.vue'

import { useFormatting } from '@/composables/useFormatting'

const props = defineProps({
  isPriceLoading: {
    type: Boolean,
    default: false,
  },
  plusMonthlyPrice: {
    type: Number,
    default: 0,
  },
  annualPrice: {
    type: Number,
    default: 0,
  },
  proPrice: {
    type: Number,
    default: 0,
  },
  isPricingComparison: {
    type: Boolean,
    default: false,
  },
  monthlySavings: {
    type: Number,
    default: 0,
  },
})

const { formatCurrency } = useFormatting()

const hasSavings = computed(() => props.isPricingComparison && props.monthlySavings > 0)

// ---------------------------------------------------------------------------
// Assets
// ---------------------------------------------------------------------------
const iconSnowflake = new URL(`/src/assets/img/property-details/snowflake.svg`, import.meta.url)
const iconSupport = new URL('/src/assets/img/studio-plus-insights/support-icon.svg', import.meta.url)
const iconBars = new URL('/src/assets/img/homepage/BarsIcon.svg', import.meta.url)
const iconInfoDesk = new URL('/src/assets/img/studio-plus/InfoDeskIcon.svg', import.meta.url)

// Feature icons – included from Pro Package
const iconHotelOffers = new URL('/src/assets/img/landing/icons/hotel_offers_icon.svg', import.meta.url)
const iconRateInsightsLanding = new URL('/src/assets/img/landing/icons/rate_insights_icon.svg', import.meta.url)
const iconVisitorsProfile = new URL('/src/assets/img/landing/icons/visitors_profile_icon.svg', import.meta.url)
const iconContactBadge = new URL('/src/assets/img/studio-plus-insights/contact-badge-icon.svg', import.meta.url)

// ---------------------------------------------------------------------------
// Feature lists
// ---------------------------------------------------------------------------
const newFeatures = [
  { labelKey: 'subscription_feature_1', icon: iconSnowflake },
  { labelKey: 'subscription_feature_2', icon: iconSupport },
  { labelKey: 'subscription_feature_3', icon: iconBars },
  { labelKey: 'subscription_feature_4', icon: iconInfoDesk },
]

const proFeatures = [
  { labelKey: 'hotel_offers_feature', icon: iconHotelOffers },
  { labelKey: 'rate_insights_feature', icon: iconRateInsightsLanding },
  { labelKey: 'visitors_profile_feature', icon: iconVisitorsProfile },
  { labelKey: 'hotel_connect_feature', icon: iconContactBadge },
]
</script>
